import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Star, TrendingUp } from 'lucide-react';
import detectRegion from '../utils/detectRegion';
import {
  FLAG_EMOJI,
  REGION_LABEL_SHORT,
  trendingPath,
} from '../utils/regions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Small showcase card: picks ONE show from the trending TV list in the user's
 * region (logged-in user.region → navigator.language → timezone → 'global').
 * Click → /trending/<region>.
 *
 * Props:
 *   user — optional user object; used for region detection.
 *   rotate — when true, cycles through the full fetched trending list at
 *            `rotateIntervalMs` (default 5000ms). When false, picks a single
 *            random show on mount.
 *   rotateIntervalMs — interval in ms between rotations.
 *   limit — how many shows to fetch (default 10; bumped automatically to 40
 *           when rotate=true so there's a deep list to cycle through).
 */
const TrendingPickCard = ({
  user,
  rotate = false,
  rotateIntervalMs = 5000,
  limit,
}) => {
  const navigate = useNavigate();
  const fetchLimit = limit || (rotate ? 40 : 10);
  const [shows, setShows] = useState([]);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState(false);
  const [region, setRegion] = useState(() => detectRegion(user?.region));

  // Re-detect if a logged-in user with a saved region appears after first paint
  useEffect(() => {
    setRegion(detectRegion(user?.region));
  }, [user?.region]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await axios.get(`${API}/public/trending`, {
          params: { region, limit: fetchLimit },
        });
        const list = (resp.data && resp.data.shows) || [];
        if (!list.length) {
          if (!cancelled) setError(true);
          return;
        }
        if (!cancelled) {
          setShows(list);
          // Single-pick mode: random start; rotate mode: start from #1
          setIdx(rotate ? 0 : Math.floor(Math.random() * Math.min(list.length, 10)));
          setError(false);
        }
      } catch (e) {
        if (!cancelled) setError(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [region, fetchLimit, rotate]);

  // Rotation timer
  useEffect(() => {
    if (!rotate || shows.length < 2) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % shows.length);
    }, rotateIntervalMs);
    return () => clearInterval(t);
  }, [rotate, rotateIntervalMs, shows.length]);

  if (error || !shows.length) return null;

  const pick = shows[idx];
  const rank = idx + 1;

  const regionLabel = REGION_LABEL_SHORT[region] || 'the world';
  const flag = FLAG_EMOJI[region] || FLAG_EMOJI.global;
  const targetPath = trendingPath(region);

  return (
    <button
      type="button"
      className="trending-pick"
      data-testid="hero-trending-pick"
      data-region={region}
      onClick={() => navigate(targetPath)}
      aria-label={`Trending in ${regionLabel}, pick #${rank}: ${pick.title}. Click to see the full trending list.`}
    >
      <span className="region-badge" data-testid="hero-trending-pick-region">
        <span className="region-flag" aria-hidden="true">{flag}</span>
        Trending in {regionLabel}
      </span>

      <div className="pick-poster" key={`poster-${pick.id || idx}`}>
        {pick.image_url ? (
          <img src={pick.image_url} alt={`${pick.title} poster`} loading="lazy" />
        ) : (
          <div className="pick-poster-fallback">🐑</div>
        )}
        <span className="pick-rank" aria-hidden="true">#{rank}</span>
      </div>

      <div className="pick-body" key={`body-${pick.id || idx}`}>
        <span className="pick-eyebrow">
          <TrendingUp size={12} />
          {rotate ? 'Now trending' : 'Top 10 this week'}
        </span>
        <h3 className="pick-title">{pick.title}</h3>
        <div className="pick-meta">
          {pick.year && <span>{pick.year}</span>}
          {pick.year && pick.vote_average ? <span className="pick-dot">•</span> : null}
          {pick.vote_average ? (
            <span className="pick-score">
              <Star size={11} /> {pick.vote_average.toFixed(1)}
            </span>
          ) : null}
        </div>
        <span className="pick-cta" data-testid="hero-trending-pick-cta">
          See the top 10 →
        </span>
      </div>

      <style jsx>{`
        .trending-pick {
          position: relative;
          display: grid;
          grid-template-columns: 110px 1fr;
          gap: 1rem;
          align-items: center;
          width: 100%;
          padding: 1rem 1.1rem 1rem 1rem;
          padding-top: 1.75rem;
          background: linear-gradient(135deg, #181014 0%, #0b0b0d 100%);
          border: 1px solid #2a2a2a;
          border-radius: 14px;
          cursor: pointer;
          text-align: left;
          color: #fff;
          transition: border-color 0.18s, transform 0.18s, box-shadow 0.18s;
          overflow: hidden;
        }
        .trending-pick::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: 14px;
          padding: 1px;
          background: linear-gradient(135deg, rgba(230, 47, 136, 0.55), transparent 65%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
        }
        .trending-pick:hover,
        .trending-pick:focus-visible {
          transform: translateY(-2px);
          border-color: rgba(230, 47, 136, 0.55);
          box-shadow: 0 20px 50px -25px rgba(230, 47, 136, 0.5);
        }
        .trending-pick:focus-visible {
          outline: 2px solid #E62F88;
          outline-offset: 2px;
        }
        .region-badge {
          position: absolute;
          top: 0;
          left: 0;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.3rem 0.7rem 0.3rem 0.55rem;
          background: linear-gradient(120deg, #E62F88 0%, #ff4f9e 100%);
          color: #000;
          font-weight: 800;
          font-size: 0.7rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border-bottom-right-radius: 10px;
          z-index: 2;
        }
        .region-flag {
          font-size: 0.95rem;
          line-height: 1;
        }
        .pick-poster {
          position: relative;
          aspect-ratio: 2 / 3;
          width: 110px;
          background: #0a0a0a;
          border-radius: 8px;
          overflow: hidden;
        }
        .pick-poster img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .pick-poster-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
        }
        .pick-rank {
          position: absolute;
          top: 6px;
          left: 6px;
          background: linear-gradient(120deg, #E62F88 0%, #f5b942 100%);
          color: #0a0a0a;
          font-size: 0.7rem;
          font-weight: 800;
          padding: 2px 6px;
          border-radius: 5px;
          letter-spacing: 0.04em;
        }
        .pick-body {
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
          min-width: 0;
          animation: pickFadeIn 0.45s ease both;
        }
        .pick-poster {
          animation: pickFadeIn 0.45s ease both;
        }
        @keyframes pickFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pick-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          color: #E62F88;
          font-size: 0.65rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 800;
        }
        .pick-title {
          color: #fff;
          font-size: 1.05rem;
          font-weight: 800;
          line-height: 1.2;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pick-meta {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          color: #a1a1aa;
          font-size: 0.75rem;
        }
        .pick-dot { color: #444; }
        .pick-score {
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          color: #facc15;
          font-weight: 700;
        }
        .pick-cta {
          color: #E62F88;
          font-size: 0.78rem;
          font-weight: 700;
          margin-top: 0.15rem;
        }
        @media (max-width: 540px) {
          .trending-pick {
            grid-template-columns: 88px 1fr;
            padding-top: 1.7rem;
            gap: 0.85rem;
          }
          .pick-poster { width: 88px; }
        }
      `}</style>
    </button>
  );
};

export default TrendingPickCard;
